(() => {
  const wrapPreviewTab = (dialog, event) => {
    if (!dialog.open || event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
    const controls = [...dialog.querySelectorAll('button, a[href], input, select, textarea, [tabindex]')]
      .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && !element.closest('[hidden], [inert]') && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden');
    const first = controls[0], last = controls[controls.length - 1];
    if (!first) return;
    const target = event.shiftKey && document.activeElement === first ? last
      : !event.shiftKey && document.activeElement === last ? first : null;
    if (target) { event.preventDefault(); target.focus(); }
  };
  const reserveStickySpace = configurator => {
    const bar = configurator.querySelector('.bed-sticky');
    if (!bar) return;
    // This spacer belongs after the global footer, outside the product section.
    const spacer = document.createElement('div');
    spacer.setAttribute('aria-hidden','true');
    spacer.dataset.bedStickySpace = '';
    spacer.style.cssText = 'display:block;height:0;flex-shrink:0;pointer-events:none;';
    document.body.appendChild(spacer);
    let frame = null;
    let height = 0;
    let disposed = false;
    const measure = () => {
      frame = null;
      if (disposed) return;
      const next = getComputedStyle(bar).position === 'fixed' ? Math.ceil(bar.getBoundingClientRect().height) : 0;
      if (next === height) return;
      height = next;
      spacer.style.height = `${height}px`;
      document.documentElement.style.setProperty('--klove-purchase-height', `${height}px`);
    };
    const schedule = () => {
      if (!disposed && frame === null) frame = requestAnimationFrame(measure);
    };
    // Observe only the fixed bar. Changing its independent spacer cannot resize
    // the observed box and identical measurements never write layout again.
    const observer = new ResizeObserver(schedule);
    observer.observe(bar);
    window.addEventListener('resize',schedule,{passive:true});
    const cleanup = event => {
      if (!event.target.contains(configurator)) return;
      disposed = true;
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener('resize',schedule);
      document.removeEventListener('shopify:section:unload',cleanup);
      spacer.remove();
      document.documentElement.style.removeProperty('--klove-purchase-height');
    };
    document.addEventListener('shopify:section:unload',cleanup);
    measure();
  };
  const init = async () => {
    if (!document.querySelector('.kd-product-page [data-bed-config]')) return;
    await customElements.whenDefined('bed-configurator');
    document.querySelectorAll('.kd-product-page bed-configurator').forEach(configurator => {
      if (!configurator.config || !configurator.state || configurator.dataset.uxReady) return;
      configurator.dataset.uxReady = 'true';
      reserveStickySpace(configurator);
      const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
      const steps = [...configurator.querySelectorAll('.bed-step')];
      const nav = configurator.querySelector('[data-bed-step-nav]');
      const text = key => configurator.getString(key, '');
      const shortTitles = [text('dimensions_short'), ...configurator.config.groups.map(group => group.title)];
      const panels = steps.map((step, index) => {
        const title = step.querySelector('.bed-step__title, legend');
        const titleClone = title.cloneNode(true);
        titleClone.querySelectorAll('.bed-step__number, .bed-group__size').forEach(el => el.remove());
        const panel = document.createElement('details');
        panel.className = 'kd-config-step';
        panel.open = index === 0;
        const summary = document.createElement('summary');
        summary.className = 'kd-config-step__heading';
        const number = document.createElement('span'); number.className = 'kd-config-step__number'; number.textContent = String(index + 1); number.setAttribute('aria-hidden','true');
        const heading = document.createElement('span'); heading.className = 'kd-config-step__name'; heading.textContent = titleClone.textContent.trim();
        const selection = document.createElement('span'); selection.className = 'kd-config-step__selection';
        const chevron = document.createElement('span'); chevron.className = 'kd-config-step__chevron'; chevron.setAttribute('aria-hidden','true');
        summary.append(number,heading,selection,chevron);
        const content = document.createElement('div'); content.className = 'kd-config-step__content';
        while (step.firstChild) content.appendChild(step.firstChild);
        const next = document.createElement('button'); next.type = 'button'; next.className = 'kd-config-next';
        next.textContent = index < steps.length - 1 ? text('next_step').replace('[step]', shortTitles[index + 1]) : text('review_summary');
        content.appendChild(next);
        panel.append(summary,content); step.appendChild(panel);
        const navButton = document.createElement('button'); navButton.type = 'button'; navButton.textContent = `${index + 1}. ${shortTitles[index]}`;
        const open = () => {
          panel.open = true;
          summary.focus({preventScroll:true});
          panel.scrollIntoView({behavior:reduced() ? 'auto':'smooth',block:'start'});
        };
        navButton.addEventListener('click',open);
        nav?.appendChild(navButton);
        panel.addEventListener('toggle', () => {
          if (panel.open) {
            nav?.querySelectorAll('button').forEach(button => button.removeAttribute('aria-current'));
            navButton.setAttribute('aria-current','step');
          }
        });
        if (index === 0) navButton.setAttribute('aria-current','step');
        next.addEventListener('click', () => {
          const problem = configurator.validationProblems().find(item => content.contains(item.fieldset));
          if (problem) { configurator.showGroupError(problem.fieldset,problem.message); configurator.scrollToGroup(problem.fieldset); return; }
          if (index < steps.length - 1) { panel.open = false; panels[index + 1].open(); }
          else {
            const target = configurator.querySelector('.bed-summary');
            target.tabIndex = -1; target.focus({preventScroll:true});
            target.scrollIntoView({behavior:reduced() ? 'auto':'smooth',block:'start'});
          }
        });
        return {panel,selection,open};
      });
      if (nav) nav.hidden = false;
      const update = () => {
        panels.forEach(({panel,selection}, index) => {
          const labels = [...panel.querySelectorAll('input[type="radio"]:checked')].map(input => {
            const label = input.labels?.[0];
            return label?.querySelector('.bed-option__label')?.textContent.trim() || input.value;
          });
          selection.textContent = labels.length ? (index === 0 ? labels.slice().reverse().join(' × ') : labels.join(' · ')) : text('choose_option');
        });
        const preview = configurator.querySelector('[data-bed-preview-selection]');
        const fabric = configurator.config.groups.find(group => group.key === 'fabric')?.options.find(option => option.value === configurator.state.picks.fabric);
        if (preview) preview.textContent = `${configurator.state.dims[1]} × ${configurator.state.dims[0]} · ${fabric?.label || text('choose_fabric')}`;
      };
      update();
      configurator.addEventListener('change',update);
      configurator.querySelectorAll('.bed-summary__change').forEach(link => link.addEventListener('click', event => {
        const target = document.getElementById(link.hash.slice(1));
        if (!target || !configurator.contains(target)) return;
        event.preventDefault(); configurator.scrollToGroup(target);
      }));
      const productDetails = configurator.querySelector('.kd-pdp-details');
      const mobile = matchMedia('(max-width: 989px)');
      const positionDetails = () => {
        if (!productDetails) return;
        if (mobile.matches) configurator.querySelector('.bed-summary').after(productDetails);
        else configurator.querySelector('.bed-configurator__media').appendChild(productDetails);
      };
      positionDetails(); mobile.addEventListener('change',positionDetails);
      const image = configurator.querySelector('.bed-configurator__image');
      const dialog = configurator.querySelector('[data-bed-preview-dialog]');
      const zoom = configurator.querySelector('[data-bed-zoom]');
      dialog?.addEventListener('keydown', event => wrapPreviewTab(dialog, event));
      const gallery = configurator.querySelector('klove-product-gallery');
      const openPreview = (previewImage = image) => {
        if (!previewImage || !dialog) return;
        const enlarged = dialog.querySelector('img'); enlarged.src = previewImage.dataset.fullSrc || previewImage.currentSrc || previewImage.src; enlarged.alt = previewImage.alt;
        dialog.querySelector('p').textContent = previewImage.alt;
        dialog.classList.remove('is-detail');
        const detailButton = dialog.querySelector('[data-bed-detail-zoom]'); detailButton.textContent = text('zoom_details'); detailButton.setAttribute('aria-pressed','false');
        dialog.showModal();
      };
      zoom?.addEventListener('click', () => openPreview(gallery?.activeImage || image));
      dialog?.querySelector('[data-bed-detail-zoom]').addEventListener('click', event => {
        const enlarged = dialog.classList.toggle('is-detail');
        event.currentTarget.textContent = enlarged ? text('zoom_fit') : text('zoom_details');
        event.currentTarget.setAttribute('aria-pressed',String(enlarged));
        const viewport = dialog.querySelector('.kd-preview-dialog__viewport');
        viewport.focus({preventScroll:true});
        viewport.scrollLeft = enlarged ? (viewport.scrollWidth - viewport.clientWidth) / 2 : 0;
        viewport.scrollTop = enlarged ? (viewport.scrollHeight - viewport.clientHeight) * .7 : 0;
      });
      dialog?.querySelector('[data-bed-close-zoom]').addEventListener('click', () => dialog.close());
      dialog?.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
      const dock = configurator.querySelector('[data-bed-mobile-preview]');
      const frame = configurator.querySelector('.kd-preview-frame');
      const page = configurator.closest('.kd-product-page');
      const header = document.querySelector('.kd-global-header');
      const summaryBlock = configurator.querySelector('.bed-summary');
      if (dock && frame && image && page) {
        const dockImage = dock.querySelector('[data-bed-dock-image]');
        const selection = dock.querySelector('[data-bed-dock-selection]');
        const collapse = dock.querySelector('[data-bed-dock-collapse]');
        const synchronize = () => {
          const src = image.getAttribute('src');
          if (src && dockImage.getAttribute('src') !== src) dockImage.src = src;
          dockImage.alt = image.alt;
          selection.textContent = configurator.querySelector('[data-bed-preview-selection]')?.textContent || '';
          const loading = image.getAttribute('aria-busy') === 'true';
          dock.dataset.loading = String(loading);
          dock.querySelector('[data-bed-dock-loading]').hidden = !loading;
        };
        let scheduled = false;
        let dockFrame = null;
        let disposed = false;
        let dockHeight = 0;
        const measureDock = () => {
          if (!mobile.matches) return;
          const wasHidden = dock.hidden;
          dock.hidden = false;
          dockHeight = dock.getBoundingClientRect().height;
          dock.hidden = wasHidden;
        };
        // One initial measurement reserves space for direct step navigation; scrolling uses the cache.
        measureDock();
        const position = () => {
          if (disposed) return;
          const headerBottom = Math.max(0,header?.getBoundingClientRect().bottom || 0);
          const visible = mobile.matches && frame.getBoundingClientRect().bottom <= headerBottom + 8 && summaryBlock.getBoundingClientRect().bottom > headerBottom + 80;
          const height = mobile.matches ? dockHeight : 0;
          page.style.setProperty('--kd-preview-top',`${headerBottom}px`);
          if (dock.hidden === visible) dock.hidden = !visible;
          page.dataset.mobilePreview = String(visible);
          page.style.setProperty('--kd-config-scroll-offset',`${headerBottom + height + 16}px`);
        };
        const schedule = () => {
          if (disposed || scheduled) return;
          scheduled = true;
          dockFrame = requestAnimationFrame(() => {
            dockFrame = null;
            scheduled = false;
            position();
          });
        };
        dock.querySelector('[data-bed-dock-zoom]').addEventListener('click',() => openPreview(image));
        collapse.addEventListener('click',() => {
          const compact = dock.classList.toggle('is-compact');
          collapse.setAttribute('aria-expanded',String(!compact));
          collapse.setAttribute('aria-label',compact ? text('preview_expand') : text('preview_collapse'));
          collapse.querySelector('span').textContent = compact ? '+' : '−';
          position();
        });
        image.addEventListener('load',synchronize);
        const imageObserver = new MutationObserver(synchronize);
        imageObserver.observe(image,{attributes:true,attributeFilter:['src','aria-busy','alt']});
        configurator.addEventListener('change',synchronize);
        const resizeObserver = new ResizeObserver(entries => {
          const dockEntry = entries.find(entry => entry.target === dock);
          if (dockEntry?.contentRect.height) dockHeight = dockEntry.borderBoxSize?.[0]?.blockSize || dockEntry.contentRect.height;
          schedule();
        });
        [frame,summaryBlock,dock,header].filter(Boolean).forEach(element => resizeObserver.observe(element));
        window.addEventListener('scroll',schedule,{passive:true});
        window.addEventListener('resize',schedule,{passive:true});
        const onViewportChange = () => { measureDock(); schedule(); };
        mobile.addEventListener('change',onViewportChange);
        synchronize(); position();
        const cleanup = event => {
          if (!event.target.contains(configurator)) return;
          disposed = true;
          if (dockFrame !== null) cancelAnimationFrame(dockFrame);
          dockFrame = null;
          imageObserver.disconnect(); resizeObserver.disconnect();
          window.removeEventListener('scroll',schedule); window.removeEventListener('resize',schedule);
          mobile.removeEventListener('change',onViewportChange);
          document.removeEventListener('shopify:section:unload',cleanup);
        };
        document.addEventListener('shopify:section:unload',cleanup);
      }

    });
  };
  init();
  document.addEventListener('shopify:section:load',init);
})();
